import { getDroppedFilePathSegments } from '@/user_files/drive/utils/get-dropped-file-path-segments'
import { isOsJunkFile } from '@/user_files/drive/utils/is-os-junk-file'

export type NestedFileGroup = { segments: string[]; files: File[] }

export type GroupedDroppedFiles = { currentFolderFiles: File[]; nestedGroups: NestedFileGroup[] }

/**
 * Groups dropped files by their target folder: the currently open folder, or a nested subfolder
 * to recreate. OS-generated metadata files (.DS_Store, Thumbs.db, ...) that a dropped folder's
 * directory walk pulled in are dropped entirely rather than surfaced as "unsupported type" noise.
 */
export function groupDroppedFilesByTarget(files: File[]): GroupedDroppedFiles {
  const currentFolderFiles: File[] = []
  const nestedGroupsBySegmentKey = new Map<string, NestedFileGroup>()

  for (const file of files) {
    const segments = getDroppedFilePathSegments(file)

    // What makes a file junk is that the directory walk pulled it in, not its name — a
    // dot-prefixed file the user picked or dragged deliberately (.env, .contract) stays in the
    // batch and surfaces as a normal row instead of silently vanishing.
    if (segments.length > 0 && isOsJunkFile(file)) continue

    if (segments.length === 0) {
      currentFolderFiles.push(file)
      continue
    }

    const key = segments.join('/')
    const existingGroup = nestedGroupsBySegmentKey.get(key)
    if (existingGroup) {
      existingGroup.files.push(file)
    } else {
      nestedGroupsBySegmentKey.set(key, { segments, files: [file] })
    }
  }

  return { currentFolderFiles, nestedGroups: [...nestedGroupsBySegmentKey.values()] }
}
